import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import type { SavedViewRow } from '@aurora-pvi/contracts';
import { createHttpReportingRepository } from '../api/httpReportingRepository';
import { useSession } from '../app/providers/SessionProvider';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

/**
 * A name for the filter combination somebody keeps rebuilding.
 *
 * Deliberately not a report builder. The global filters already live in the
 * URL — that is why a filtered view is shareable, a drill-down inherits its
 * parent's narrowing and an export reproduces what was on screen. All that was
 * missing was a name, and this is persistence over a mechanism that exists
 * rather than a second mechanism beside it.
 *
 * The property worth stating: a saved view stores the *question*, never the
 * *answer*. Opening one is a navigation, so the rows come back through the
 * opener's own span of control — a narrower colleague opening the same saved
 * view gets their own, smaller answer. Storing rows, or storing the saver's
 * scope alongside the filters, would turn a convenience into a disclosure.
 */
export function SavedViews() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useSession();
  const queryClient = useQueryClient();
  const client = useRef(API_BASE_URL ? createHttpReportingRepository(API_BASE_URL) : null);

  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['saved-views', currentUser.id],
    queryFn: () => {
      if (!client.current) throw new Error('unconnected');
      return client.current.listSavedViews();
    },
    enabled: Boolean(client.current) && open,
  });

  const rows = (query.data?.data ?? []) as SavedViewRow[];

  const save = () => {
    void (async () => {
      if (!client.current) return;
      setBusy(true);
      setError(null);
      const written = await client.current.saveView({
        name: name.trim(),
        viewRoute: location.pathname,
        viewQuery: location.search.replace(/^\?/, ''),
      });
      setBusy(false);
      if (!written.ok) {
        setError(written.message);
        return;
      }
      setName('');
      await queryClient.invalidateQueries({ queryKey: ['saved-views'] });
    })();
  };

  if (!client.current) return null;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((previous) => !previous)}
        aria-expanded={open}
        aria-label="Saved views"
        className="flex h-9 items-center gap-1.5 rounded-[10px] border border-[var(--g-edge)] px-2.5 text-[12px] text-[var(--g-text-secondary)] transition-colors hover:text-[var(--g-text)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--g-accent)]"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M6 4h12v16l-6-4-6 4z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
        </svg>
        Views
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label="Saved views"
          className="absolute right-0 z-50 mt-2 w-[320px] rounded-[var(--g-radius)] border border-[var(--g-edge)] bg-[var(--g-surface-solid)] p-3 shadow-lg"
        >
          <p className="g-label">Save this view</p>
          <div className="mt-1.5 flex items-center gap-2">
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Name it"
              className="h-8 min-w-0 flex-1 rounded-[8px] border border-[var(--g-edge)] bg-[var(--g-surface-2)] px-2 text-[12px] text-[var(--g-text)]"
            />
            <button
              type="button"
              onClick={save}
              disabled={busy || name.trim().length < 2}
              title={name.trim().length < 2 ? 'Give it a name of at least two characters.' : undefined}
              className="h-8 shrink-0 rounded-[8px] bg-[var(--g-accent)] px-2.5 text-[12px] font-medium text-[var(--g-on-accent,#fff)] disabled:opacity-45"
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
          <p className="mt-1 text-[10.5px] leading-relaxed text-[var(--g-text-muted)]">
            Saves the filters, not the rows. Whoever opens it sees their own span of control.
          </p>
          {error ? <p className="mt-1 text-[11px] text-[var(--g-critical)]">{error}</p> : null}

          <div className="mt-3 border-t border-[var(--g-edge)] pt-2">
            {query.isPending ? (
              <p className="text-[11.5px] text-[var(--g-text-muted)]">Loading your views…</p>
            ) : rows.length === 0 ? (
              <p className="text-[11.5px] leading-relaxed text-[var(--g-text-muted)]">
                You have not saved a view yet.
              </p>
            ) : (
              <ul className="flex max-h-64 flex-col gap-0.5 overflow-y-auto">
                {rows.map((row) => (
                  <li key={row.id} className="flex items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        void client.current?.markViewOpened(row.id);
                        navigate(
                          row.viewQuery ? `${row.viewRoute}?${row.viewQuery}` : row.viewRoute,
                        );
                        setOpen(false);
                      }}
                      className="min-w-0 flex-1 truncate rounded-[6px] px-1.5 py-1 text-left text-[12px] text-[var(--g-text)] hover:bg-[var(--g-surface-2)]"
                      title={`${row.viewRoute}${row.viewQuery ? `?${row.viewQuery}` : ''}`}
                    >
                      {row.name}
                    </button>
                    <button
                      type="button"
                      aria-label={`Delete ${row.name}`}
                      onClick={() => {
                        void (async () => {
                          await client.current?.deleteSavedView(row.id);
                          await queryClient.invalidateQueries({ queryKey: ['saved-views'] });
                        })();
                      }}
                      className="shrink-0 rounded-[6px] px-1.5 py-1 text-[11px] text-[var(--g-text-muted)] hover:text-[var(--g-critical)]"
                    >
                      Delete
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
